import { useTranslation } from 'react-i18next'
import { Tooltip } from '@/components/ui/Tooltip'
import { EyeIcon, EyeOffIcon, TrashIcon } from '@/components/ui/icons'
import type { Table } from '@/types'
import { SidebarColumnItem } from './SidebarColumnItem'

interface SidebarTableItemProps {
  table: Table
  displayName: string
  selected: boolean
  hidden: boolean
  expanded: boolean
  accentColor: string
  onToggleExpand: () => void
  onSelect: () => void
  onColumnClick: () => void
  onToggleVisibility: () => void
  onEdit: () => void
  onDelete: () => void
  readOnly?: boolean
}

export function SidebarTableItem({
  table,
  displayName,
  selected,
  hidden,
  expanded,
  accentColor,
  onToggleExpand,
  onSelect,
  onColumnClick,
  onToggleVisibility,
  onEdit,
  onDelete,
  readOnly = false,
}: SidebarTableItemProps) {
  const { t } = useTranslation()
  const tooltipText = readOnly
    ? table.fullName || displayName
    : `${table.fullName || displayName} • ${t('sidebar.doubleClickToEdit')}`
  const handleActionClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    action: () => void,
  ) => {
    action()
    event.currentTarget.blur()
  }

  return (
    <div>
      <div
        className="group flex items-center gap-1 border-l-2 pr-2"
        style={{ borderLeftColor: accentColor }}
      >
        <button
          type="button"
          onClick={onToggleExpand}
          className="inline-flex h-7 w-5 shrink-0 items-center justify-center text-[8px]"
          style={{ color: 'var(--text-dim)' }}
          aria-label={expanded ? t('sidebar.collapse') : t('sidebar.expand')}
          title={expanded ? t('sidebar.collapse') : t('sidebar.expand')}
        >
          <span
            className="transition-transform"
            style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
          >
            ▶
          </span>
        </button>
        <div
          className="flex min-w-0 flex-1 items-center gap-1 rounded-md px-2 py-1 text-[11px] transition-colors"
          style={{
            background: selected ? 'rgba(79,70,229,.15)' : 'transparent',
            color: selected ? 'var(--accent)' : 'var(--text-muted)',
            opacity: hidden ? 0.62 : 1,
          }}
          onMouseEnter={(event) => {
            if (selected) return
            event.currentTarget.style.background = 'var(--sb-hover)'
            event.currentTarget.style.color = 'var(--text)'
          }}
          onMouseLeave={(event) => {
            if (selected) return
            event.currentTarget.style.background = 'transparent'
            event.currentTarget.style.color = 'var(--text-muted)'
          }}
        >
          <div className="min-w-0 flex-1">
            <Tooltip content={tooltipText} delayMs={250}>
              <button
                type="button"
                onClick={onSelect}
                onDoubleClick={readOnly ? undefined : onEdit}
                className="block w-full min-w-0 overflow-hidden text-left"
                style={{ color: 'inherit' }}
              >
                <span className="block truncate">{displayName}</span>
              </button>
            </Tooltip>
          </div>
          <span className="ml-auto flex w-11 shrink-0 items-center justify-end gap-1">
            <button
              type="button"
              onClick={(event) => handleActionClick(event, onToggleVisibility)}
              className={`inline-flex h-5 w-5 items-center justify-center transition-opacity ${
                hidden
                  ? 'opacity-100'
                  : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
              }`}
              style={{ color: hidden ? 'var(--accent)' : 'var(--text-dim)' }}
              title={hidden ? t('sidebar.showTable') : t('sidebar.hideTable')}
            >
              {hidden ? <EyeOffIcon /> : <EyeIcon />}
            </button>
            {!readOnly ? (
              <button
                type="button"
                onClick={(event) => handleActionClick(event, onDelete)}
                className="inline-flex h-5 w-5 items-center justify-center opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                style={{ color: 'var(--color-danger)' }}
                title={t('common.delete')}
              >
                <TrashIcon />
              </button>
            ) : null}
          </span>
        </div>
      </div>
      {expanded ? (
        <div>
          {table.cols.map((column) => (
            <SidebarColumnItem
              key={`${table.id}:${column.name}`}
              column={column}
              onClick={onColumnClick}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}
